import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { getChartData, getMetaDataForNewsLetter, getTableData } from './getDataForNewsLetterView';
import { Breadcrumb, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import NewsLetterTable from '../administration/components/NewsLetterTable';
import { referenceDataSyncService } from '../../services/referenceDataSyncService';
import dayjs from 'dayjs';
import CoverPage from './CoverPage';
import PrimarySquareBtn from '../../components/buttons/PrimarySquareBtn';
import { DownloadOutlined } from '@ant-design/icons';
import React from 'react';
import { exportElementToMultiPagePDF } from '../../utils/pdfDownloadUtils';
import { flushSync } from 'react-dom';
import PriceVsVarianceChartApex from '../administration/components/PriceVsVarianceChart_apex';

interface SectionPages {
  [sectionId: string]: number;
}

const NewsLetterView = () => {
  const locationRoute = useLocation();
  const { id } = useParams();

  const [loading, setLoading] = useState<any>(true);

  const [basket, setBasket] = useState<string>('');

  const [startDate, setStartDate] = useState<string>();

  const [endDate, setEndDate] = useState<string>();

  const [establishments, setEstablishments] = useState<string[]>([]);

  const [tableData, setTableData] = useState<any>([]);

  const [chartData, setChartData] = useState<any>([]);

  const { t } = useTranslation(['newsLetterView']);
  const [navigateBackTo, setNavigateBackTo] = useState('home');

  const [newsLetterMetaData, setNewsLetterMetaData] = useState<{
    id: number;
    title: string;
    summary: string;
    data: any;
    isActive: boolean;
    tableDesc: string;
    establishmentDesc: { [key: number]: string };
  }>();

  // const [sectionPages, setSectionPages] = useState<Record<string, number>>({});
  // const pdfContentRef = useRef<HTMLDivElement>(null);
  const [pdfLoading, setpdfLoading] = useState<any>(false);

  const coverRef = useRef<HTMLDivElement>(null);
  const tableWithDescRef = useRef<HTMLDivElement>(null);
  const chartRefs = useRef<HTMLDivElement[]>([]);

  // Scroll to the top onload
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const navigatedFrom = locationRoute.state;
    setNavigateBackTo(navigatedFrom);
  }, [locationRoute.state]);

  useEffect(() => {
    const getMetaDataForNewsLetterWrapper = async () => {
      if (id) {
        try {
          const res = await getMetaDataForNewsLetter(id);
          if (res.statusText === 'SUCCESS') {
            setNewsLetterMetaData(res.data);
            const basketId = res?.data?.data?.filters?.basketId;
            const basket = await referenceDataSyncService.getBasketDataById(basketId);
            const basketName = basket[0]?.data?.name;
            setBasket(basketName);
            setStartDate(res?.data?.data?.filters?.startDate);
            setEndDate(res?.data?.data?.filters?.endDate);

            const establishmentsIds = res?.data?.data?.filters?.establishmentIds;

            const establishmentNames: string[] = [];

            for (const id of establishmentsIds) {
              const tempData = await referenceDataSyncService.getEstablishmentDataById(id);

              establishmentNames.push(tempData[0].name);
            }
            setEstablishments(establishmentNames);
          }
        } catch (error: any) {}
      }
    };

    getMetaDataForNewsLetterWrapper();
  }, []);

  useEffect(() => {
    const getTableDataAndChartData = async () => {
      if (newsLetterMetaData) {
        try {
          setLoading(true);
          const [tempTableData, tempChartData] = await Promise.all([
            getTableData(newsLetterMetaData.data?.filters),
            getChartData(newsLetterMetaData.data?.filters),
          ]);

          setTableData(tempTableData.data);
          setChartData(tempChartData.data);
          chartRefs.current = tempChartData.data
            .map(() => React.createRef<HTMLDivElement>().current!)
            .filter(Boolean);
        } catch (error) {
        } finally {
          setLoading(false);
        }
      }
    };

    getTableDataAndChartData();
  }, [newsLetterMetaData]);

  /*  const tocItems = useMemo(
    () => ['Data Table', ...chartData.map((item: { establishment: any }) => item.establishment)],
    [chartData]
  ); */

  const handleDownloadPDF = async () => {
    const element = document.getElementById('newsletter');
    if (!element) {
      console.error("Element with id 'newsletter' not found.");
      return;
    }
    flushSync(() => setpdfLoading(true));

    await new Promise((r) => requestAnimationFrame(() => r(null)));
    await new Promise((r) => requestAnimationFrame(() => r(null)));

    try {
      await exportElementToMultiPagePDF(element, {
        marginPx: 20,
        backgroundColor: '#FFF',
        fileName: `${newsLetterMetaData?.title || 'Newsletter'}.pdf`,
        forceLandscape: true,
      });
    } catch (e) {
      console.error('Error downloading PDF:', e);
    } finally {
      setpdfLoading(false);
    }
  };

  /* const handleDownload = async () => {
    setpdfLoading(true);
    try {
      if (!coverRef.current || !tableWithDescRef.current) return;

      await new Promise((res) => setTimeout(res, 500)); // wait charts render

      const chartEls = chartRefs.current.filter(Boolean) as HTMLElement[];
      const chartTitles = chartData.map(
        (chart: { establishment: any }) => `${chart.establishment} Establishment`
      );
      await generatePDF(
        coverRef.current,
        tableWithDescRef.current,
        chartEls,
        chartTitles,
        `${newsLetterMetaData?.title || 'Newsletter'}.pdf`
      );
    } catch (err) {
      console.error('PDF generation failed', err);
    } finally {
      setpdfLoading(false);
    }
  };
 */

  return (
    <div className="min-h-[80vh] bg-white px-6 py-8 pb-16 sm:px-16">
      <div className="mb-6 mt-2 w-full">
        <Breadcrumb separator=">">
          <Breadcrumb.Item>
            <Link to={`/${navigateBackTo}`} className="navbar-option">
              {navigateBackTo === 'home' ? t('home') : t('administration')}
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item className="text-primary-font-color">
            {t('newsletterView')}
          </Breadcrumb.Item>
        </Breadcrumb>
      </div>
      {/* <h1 className="text-[64px] font-medium">{newsLetterMetaData!.title}</h1>
      <h5 className="mt-6 text-xl font-medium">{newsLetterMetaData!.summary}</h5> */}

      {pdfLoading && (
        <div className="absolute inset-0 z-50 flex h-full flex-col items-center justify-center bg-black bg-opacity-40">
          <div className="mb-4 text-xl font-semibold text-white">{t('generatingPDF')}</div>
          <div className="loader h-12 w-12 animate-spin rounded-full border-4 border-white border-t-transparent"></div>
        </div>
      )}

      {!loading && (
        <div className="mb-4 flex justify-end gap-4">
          <PrimarySquareBtn handleOnClick={handleDownloadPDF}>
            <div className="flex">
              <DownloadOutlined className="h-6 w-6 text-white" />
              <p className="pt-1">{t('downloadNewsletterPDF')}</p>
            </div>
          </PrimarySquareBtn>
        </div>
      )}

      <div id="newsletter">
        <div ref={coverRef} className="mb-4">
          {!loading && (
            <CoverPage
              title={newsLetterMetaData?.title}
              startDate={dayjs(Number(startDate)).format('YYYY-MM-DD')}
              endDate={dayjs(Number(endDate)).format('YYYY-MM-DD')}
            />
          )}
        </div>

        {/* <Row gutter={[16, 16]} className="my-10">
        <Col xs={24} sm={12} md={8} lg={4}>
          <div className="mb-2">
            <label className="text-sm font-medium text-gray-700">{t('basket')}</label>
          </div>
          <Input value={basket} defaultValue={basket} disabled />
        </Col>

        <Col xs={24} sm={12} md={8} lg={4}>
          <div className="mb-2">
            <label className="text-sm font-medium text-gray-700">{t('startDate')}</label>
          </div>
          <Input value={dayjs(Number(startDate)).format('YYYY-MM-DD')} className="w-full [&>.ant-picker]:!rounded-lg" disabled />
        </Col>

        <Col xs={24} sm={12} md={8} lg={4}>
          <div className="mb-2">
            <label className="text-sm font-medium text-gray-700">{t('endDate')}</label>
          </div>
          <Input value={dayjs(Number(endDate)).format('YYYY-MM-DD')} className="w-full [&>.ant-picker]:!rounded-lg" disabled />
        </Col>

        <Col xs={24} sm={12} md={8} lg={8}>
          <div className="mb-2">
            <label className="text-sm font-medium text-gray-700">{t('establishments')}</label>
          </div>
          <CustomTextbox
            text={`${establishments.join(', ')}`}
          />
        </Col>
      </Row> */}
        {newsLetterMetaData?.summary && (
          <div id="summary" className="mt-4">
            <h4 className="mb-2 text-xl !font-semibold">{t('summary')}</h4>
            <p>{newsLetterMetaData?.summary}</p>
          </div>
        )}

        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Spin size="large" />
          </div>
        ) : (
          <div ref={tableWithDescRef} id="table-container" className="mb-4">
            <NewsLetterTable
              data={tableData}
              selectedStartDate={startDate}
              selectedEndDate={endDate}
              selectedEstablishments={establishments}
              // data-export-mode={pdfLoading ? 'true' : 'false'}
            />
            {newsLetterMetaData?.data?.tableDesc && (
              <div className="table-description mb-4 mt-4">
                <h4 className="mb-2 text-lg !font-semibold">{t('tableDescription')}</h4>
                <p>{newsLetterMetaData?.data?.tableDesc}</p>
              </div>
            )}
          </div>
        )}
        {chartData &&
          chartData.map((item: any, i: any) => {
            return (
              <>
                {item?.priceVariance.length > 0 && (
                  <div
                    className="chart-wrapper mb-4"
                    key={item.establishmentId}
                    ref={(el) => (chartRefs.current[i] = el!)}
                  >
                    <PriceVsVarianceChartApex
                      establishment={item?.establishment}
                      priceVarianceArr={item?.priceVariance || []}
                      establishmentId={item?.establishmentId}
                      description={
                        newsLetterMetaData?.data.establishmentDesc[item?.establishmentId]
                      }
                      mode={'view'}
                      data-export-mode={pdfLoading ? 'true' : 'false'}
                    />

                    {newsLetterMetaData?.data.establishmentDesc[item?.establishmentId] && (
                      <div className="chart-description">
                        <p>{newsLetterMetaData?.data.establishmentDesc[item?.establishmentId]}</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })}
        {/* </div> */}
      </div>
    </div>
  );
};

export default NewsLetterView;
